var _class, _InternalDiscoveryUnitsContainer;
import { observer, Provider } from 'mobx-react';
import React from 'react';
import { discoveryTracker } from '@udemy/browse-event-tracking';
import { sortChildren } from '@udemy/browse-utils';
import { AboveTheFoldProvider } from '@udemy/design-system-utils';
import { TrackingContextProvider } from '@udemy/event-tracking';
import { withI18n } from '@udemy/i18n';
import { AlertBanner } from '@udemy/react-messaging-components';
import { withUDData } from '@udemy/ud-data';
import { InfiniteScrollContainer } from '../infinite-scroll-container/infinite-scroll-container.react-component';
import { DiscoveryUnitRenderer } from './discovery-unit-renderer.react-component';
import { DiscoveryUnitsContainerStore } from './discovery-units-container.mobx-store';
import styles from "./discovery-units-container.module.css";
import { DiscoveryUnitsLoadingSkeleton } from './discovery-units-loading-skeleton.react-component';
export let InternalDiscoveryUnitsContainer = observer(_class = (_InternalDiscoveryUnitsContainer = class InternalDiscoveryUnitsContainer extends React.Component {
  constructor(props) {
    super(props);
    this.store = void 0;
    this.renderUnit = unit => {
      var _this$props$className, _this$props$itemIndic;
      const {
        pageType,
        getUnitComponentAndProps
      } = this.props;
      const {
        component,
        componentProps,
        contextObject
      } = getUnitComponentAndProps(unit);
      if (!component) {
        throw new Error(`Missing discovery unit component for ${unit.type}`);
      }
      return /*#__PURE__*/React.createElement(DiscoveryUnitRenderer, Object.assign({}, this.props, {
        alternateHeadline: this.props.alternateHeadline,
        className: (_this$props$className = this.props.className) !== null && _this$props$className !== void 0 ? _this$props$className : styles['component-margin'],
        "data-item-index": unit.type && ((_this$props$itemIndic = this.props.itemIndicesByType) === null || _this$props$itemIndic === void 0 ? void 0 : _this$props$itemIndic[unit === null || unit === void 0 ? void 0 : unit.type]),
        deviceType: this.props.deviceType,
        pageType: pageType,
        showTitle: this.props.showTitle,
        unit: unit,
        component: component,
        componentProps: componentProps,
        contextObject: contextObject
      }));
    };
    this.handleLastChildEnter = () => {
      if (this.props.disableInfiniteScroll) {
        return;
      }
      const options = {
        pageSize: 6,
        // Use the same item count as UDHeavy. We should consider lowering the item
        // count for all pages in the future to reduce page load time.
        itemCount: this.props.itemCount,
        ...this.props.fetchOptions
      };
      this.store.fetchUnits(options);
    };
    this.refreshPage = () => {
      window.location.reload();
    };
    const {
      pageType: _pageType,
      pageObjectId,
      pageObject,
      units
    } = props;
    this.store = props.store || new DiscoveryUnitsContainerStore({
      pageType: _pageType,
      units,
      pageObjectId,
      pageObject
    }, this.props.udData);
  }
  componentDidMount() {
    // only perform initital fetch if there is no initial data
    if (!this.props.units.length) {
      const options = {
        pageSize: 6,
        // We should consider lowering the item count for all pages in the future
        // to reduce page load time.
        itemCount: this.props.itemCount,
        ...this.props.fetchOptions
      };
      this.store.fetchUnits(options);
    } else {
      this.store.processPreloadedUnits();
    }
  }
  render() {
    const {
      gettext,
      pgettext,
      filter,
      children
    } = this.props;
    const SkeletonComponent = this.props.skeletonComponent;
    if (this.store.error) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AlertBanner, {
        title: gettext('There was a problem loading course recommendations'),
        body: gettext('Please reload the page to resolve this issue'),
        ctaText: pgettext('e.g. Refresh a webpage', 'Reload Page'),
        onAction: this.refreshPage,
        udStyle: "warning"
      }), /*#__PURE__*/React.createElement(SkeletonComponent, {
        className: "component-margin"
      }));
    }
    if (this.store.firstLoad || this.props.isLoading) {
      return /*#__PURE__*/React.createElement(SkeletonComponent, {
        className: "component-margin"
      });
    }
    if (!this.store.units.length) {
      if (!this.props.alwaysShowInjectedComponents) {
        return null;
      }
      if (!React.Children.toArray(children).filter(child => /*#__PURE__*/React.isValidElement(child)).length) {
        return null;
      }
    }
    return /*#__PURE__*/React.createElement(Provider, {
      discoveryUnitsStore: this.store
    }, /*#__PURE__*/React.createElement(TrackingContextProvider, {
      trackingContext: {
        trackImpressionFunc: discoveryTracker.trackDiscoveryImpression,
        backendSource: this.store.backendSource
      }
    }, /*#__PURE__*/React.createElement(InfiniteScrollContainer, {
      onLastChildEnter: this.handleLastChildEnter
    }, sortChildren([...this.store.units.filter(filter).map(this.renderUnit), ...React.Children.toArray(children)]).filter(Boolean).map((child, i) => /*#__PURE__*/React.createElement(DiscoveryUnitWrapper, {
      shouldSendPerfMetric: i === 0,
      key: i
    }, this.props.firstUnitAboveTheFold ? /*#__PURE__*/React.createElement(AboveTheFoldProvider, null, child) : child)))));
  }
}, _InternalDiscoveryUnitsContainer.defaultProps = {
  alternateHeadline: undefined,
  alwaysShowInjectedComponents: false,
  disableInfiniteScroll: false,
  fetchOptions: {},
  filter: () => true,
  firstUnitAboveTheFold: false,
  itemCount: 12,
  itemIndicesByType: {},
  isLoading: false,
  pageObject: {},
  pageObjectId: null,
  showTitle: true,
  skeletonComponent: DiscoveryUnitsLoadingSkeleton,
  store: undefined,
  units: [],
  className: undefined
}, _InternalDiscoveryUnitsContainer)) || _class;
// mobx-react `Provider` is a functional component. We need a class component because
// the react-intersection-observer `Observer` inside InfiniteScrollContainer uses `ref`.
class DiscoveryUnitWrapper extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(Provider, {
      shouldSendPerfMetric: this.props.shouldSendPerfMetric
    }, this.props.children);
  }
}
let LocalInternalDiscoveryUnitsContainer = withUDData(InternalDiscoveryUnitsContainer);
LocalInternalDiscoveryUnitsContainer = withI18n(LocalInternalDiscoveryUnitsContainer);
export { LocalInternalDiscoveryUnitsContainer as DiscoveryUnitsContainer };
//# sourceMappingURL=discovery-units-container.react-component.js.map